import React, {useCallback, useEffect, useRef, useState} from "react";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {apiV2} from "@/api/axiosConfig";
import {isAxiosError} from "axios";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useAuth} from "@/context/user";
import {Client} from "@/types/userManagement";
import {ClientSelectV2} from "@/lib/clients/ClientSelectV2";
import {parseKMLFile} from "@/lib/kml-parser";
import {useIncidentFeeds} from "@/hooks/useIncidentFeeds";
import {KMLPreviewModal} from "@/components/map/KMLPreviewModal";
import {DepartmentName} from "@/lib/departments/DepartmentName";
import {UserName} from "@/lib/users/UserName";
import {NotificationGroupName} from "@/lib/notification-groups/NotificationGroupName";

type ParsedRow = {
    name?: string;
    locationType?: string;
    countryCodes?: string;
    departmentID?: string;
    facilityIDs?: string;
    radius?: string | number;
    notifyFacilityGuardians?: string | boolean;
    notifyTrackingTeam?: string | boolean;
    incidentFeedIDs?: string;
    incidentTags?: string;
    excludedTags?: string;
    incidentTagMode?: string;
    incidentTypes?: string;
    incidentCategories?: string; // Deprecated - kept for backward compatibility
    incidentSeverities?: string;
    alertRecipients?: string;
    alertNotificationGroups?: string;
    id?: string;
    [key: string]: any;
};

type CustomShape = {
    type: "circle" | "polygon" | "polyline";
    center?: number[]; // [lng, lat] for circles
    radius?: number; // radius in meters for circles
    geometry?: number[][]; // [[lng, lat], ...] for polygons/polylines
};

type ImportRow = ParsedRow & {
    rowNumber: number;
    status: "pending" | "processing" | "success" | "error";
    error?: string;
    alertID?: string;
    kmlFile?: File;
    customShapes?: CustomShape[];
};

type ImportStats = {
    total: number;
    pending: number;
    processing: number;
    success: number;
    error: number;
};

type ImportTileProps = {
    row: ImportRow;
    index: number;
    onKMLFileChange: (file: File | undefined, shapes?: CustomShape[]) => Promise<void>;
    getStatusColor: (status: ImportRow["status"]) => string;
    getStatusBadge: (status: ImportRow["status"]) => React.ReactNode;
    isImporting: boolean;
};

const ImportTile: React.FC<ImportTileProps> = ({
                                                   row,
                                                   index,
                                                   onKMLFileChange,
                                                   getStatusColor,
                                                   getStatusBadge,
                                                   isImporting
                                               }) => {
    const [isDraggingKML, setIsDraggingKML] = useState(false);
    const [isParsingKML, setIsParsingKML] = useState(false);
    const [kmlParseError, setKmlParseError] = useState<string | undefined>();
    const [showPreview, setShowPreview] = useState(false);

    // Disable KML changes if importing or row has been processed
    const isKMLDisabled = isImporting || row.status === "success" || row.status === "error";

    const handleKMLDragEnter = useCallback((e: React.DragEvent) => {
        if (isKMLDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingKML(true);
    }, [isKMLDisabled]);

    const handleKMLDragLeave = useCallback((e: React.DragEvent) => {
        if (isKMLDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingKML(false);
    }, [isKMLDisabled]);

    const handleKMLDragOver = useCallback((e: React.DragEvent) => {
        if (isKMLDisabled) return;
        e.preventDefault();
        e.stopPropagation();
    }, [isKMLDisabled]);

    const handleKMLDrop = useCallback(async (e: React.DragEvent) => {
        if (isKMLDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingKML(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            const extension = droppedFile.name.split(".").pop()?.toLowerCase();
            if (extension === "kml" || extension === "kmz") {
                setIsParsingKML(true);
                setKmlParseError(undefined);
                try {
                    const shapes = await parseKMLFile(droppedFile);
                    await onKMLFileChange(droppedFile, shapes);
                } catch (error) {
                    console.error("Error parsing KML:", error);
                    setKmlParseError(error instanceof Error ? error.message : "Failed to parse KML file");
                } finally {
                    setIsParsingKML(false);
                }
            } else {
                alert("Please upload a KML or KMZ file");
            }
        }
    }, [onKMLFileChange, isKMLDisabled]);

    const handleKMLFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isKMLDisabled) return;
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setIsParsingKML(true);
            setKmlParseError(undefined);
            try {
                const shapes = await parseKMLFile(selectedFile);
                await onKMLFileChange(selectedFile, shapes);
            } catch (error) {
                console.error("Error parsing KML:", error);
                setKmlParseError(error instanceof Error ? error.message : "Failed to parse KML file");
            } finally {
                setIsParsingKML(false);
            }
        }
    }, [onKMLFileChange, isKMLDisabled]);

    const handleRemoveKML = useCallback(async () => {
        if (isKMLDisabled) return;
        setKmlParseError(undefined);
        await onKMLFileChange(undefined);
    }, [onKMLFileChange, isKMLDisabled]);

    return (
        <div className={`bg-white shadow-md rounded-lg border-2 ${getStatusColor(row.status)} p-4 flex flex-col gap-3`}>
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <div className="text-xs text-grey">Row {row.rowNumber}</div>
                    <div className="font-semibold text-lg">{row.name || "(Unnamed)"}</div>
                    {row.departmentID && <div className="text-xs text-grey">Department: <DepartmentName departmentID={row.departmentID} /></div>}
                </div>
                {getStatusBadge(row.status)}
            </div>

            {/* Location Info */}
            {row.locationType != "custom" && (
                <div>
                    <div className="text-xs font-semibold text-grey mb-1">Location</div>
                    <div className="text-sm">
                        {/* <div className="capitalize">{row.locationType || "Not specified"}</div>*/}
                        {row.locationType === "country" && row.countryCodes && (
                            <div className="text-xs text-grey mt-1">Countries: {row.countryCodes}</div>
                        )}
                        {row.locationType === "facility" && (
                            <>
                                {row.facilityIDs &&
                                    <div className="text-xs text-grey mt-1">Facilities: {row.facilityIDs}</div>}
                                {row.radius && <div className="text-xs text-grey">Radius: {row.radius}m</div>}
                                <div className="text-xs text-grey">Notify Guardians: {(() => {
                                    const v = String(row.notifyFacilityGuardians ?? "").trim().toLowerCase();
                                    return v === "yes" || v === "true" ? "yes" : "no";
                                })()}</div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* KML Upload for Custom Location Type */}
            {row.locationType === "custom" && (
                <div>
                    <div className="text-xs font-semibold text-grey mb-1">Custom Shape</div>
                    {isParsingKML ? (
                        <div className="border-2 border-blue bg-light-blue rounded p-3 text-center">
                            <div className="text-xs">Parsing KML file...</div>
                        </div>
                    ) : !row.kmlFile ? (
                        <div
                            className={`border-2 border-dashed rounded p-3 text-center transition-colors ${
                                isKMLDisabled
                                    ? "border-light-grey bg-desaturated-grey cursor-not-allowed opacity-60"
                                    : isDraggingKML
                                        ? "border-blue bg-light-blue"
                                        : "border-light-grey"
                            }`}
                            onDragEnter={handleKMLDragEnter}
                            onDragOver={handleKMLDragOver}
                            onDragLeave={handleKMLDragLeave}
                            onDrop={handleKMLDrop}
                        >
                            <div className="text-2xl mb-1">📁</div>
                            <div className="text-xs mb-2">
                                {isKMLDisabled
                                    ? (row.status === "success" || row.status === "error"
                                        ? "Processed - cannot modify"
                                        : "Import in progress...")
                                    : "Drop KML/KMZ file"}
                            </div>
                            <label className={isKMLDisabled ? "cursor-not-allowed" : "cursor-pointer"}>
                                <span className={`text-xs px-3 py-1 rounded inline-block ${
                                    isKMLDisabled
                                        ? "bg-light-grey text-grey cursor-not-allowed"
                                        : "bg-blue text-white hover:bg-dark-blue"
                                }`}>
                                    Browse
                                </span>
                                <input
                                    type="file"
                                    accept=".kml,.kmz"
                                    onChange={handleKMLFileSelect}
                                    className="hidden"
                                    disabled={isKMLDisabled}
                                />
                            </label>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between bg-desaturated-grey rounded p-2">
                                <div className="text-xs truncate flex-1">{row.kmlFile.name}</div>
                                <div className="flex gap-1">
                                    {row.customShapes && row.customShapes.length > 0 && (
                                        <button
                                            onClick={() => setShowPreview(true)}
                                            className="text-xs px-2 py-1 bg-blue text-white rounded hover:bg-dark-blue"
                                            title="Preview shapes on map"
                                        >
                                            Preview
                                        </button>
                                    )}
                                    <button
                                        onClick={handleRemoveKML}
                                        disabled={isKMLDisabled}
                                        className={`text-xs px-2 py-1 rounded ${
                                            isKMLDisabled
                                                ? "bg-light-grey text-grey cursor-not-allowed"
                                                : "bg-red text-white hover:bg-dark-red"
                                        }`}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                            {row.customShapes && row.customShapes.length > 0 && (
                                <div className="text-xs text-grey">
                                    ✓ {row.customShapes.length} shape{row.customShapes.length !== 1 ? "s" : ""} parsed
                                </div>
                            )}
                        </div>
                    )}
                    {kmlParseError && (
                        <div
                            className="mt-2 bg-desaturated-red border border-dark-red rounded p-2 text-xs text-dark-red">
                            {kmlParseError}
                        </div>
                    )}
                </div>
            )}

            {/* Incident Configuration */}
            <div>
                <div className="text-xs font-semibold text-grey mb-1">Incident Configuration</div>
                <div className="text-xs space-y-1">
                    {row.incidentFeedIDs && <div>Feeds: {row.incidentFeedIDs}</div>}
                    {row.incidentType && <div className="capitalize">Type: {row.incidentType}</div>}
                    {row.incidentTags && <div>Tags: {row.incidentTags}</div>}
                    {row.excludedTags && <div>Excluded: {row.excludedTags}</div>}
                    {row.incidentTagMode && <div>Tag Mode: {row.incidentTagMode}</div>}
                    {row.incidentTypes && <div>Categories: {row.incidentTypes}</div>}
                    {row.incidentSeverities && <div>Severities: {row.incidentSeverities}</div>}
                </div>
            </div>

            {/* Recipients */}
            {(row.alertRecipients || row.alertNotificationGroups || row.notifyTrackingTeam) && (
                <div>
                    <div className="text-xs font-semibold text-grey mb-1">Recipients</div>
                    {row.alertRecipients && (
                        <div className="text-xs">
                            <div className="text-grey mb-0.5">Users:</div>
                            <div className="flex flex-wrap gap-1">
                                {row.alertRecipients.split(",").map(id => id.trim()).filter(Boolean).map(id => (
                                    <span key={id}><UserName userID={id} /></span>
                                ))}
                            </div>
                        </div>
                    )}
                    {row.alertNotificationGroups && (
                        <div className="text-xs">
                            <div className="text-grey mb-0.5">Groups:</div>
                            <div className="flex flex-wrap gap-1">
                                {row.alertNotificationGroups.split(",").map(id => id.trim()).filter(Boolean).map(id => (
                                    <span key={id}><NotificationGroupName groupID={id} /></span>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="text-xs text-grey">Notify Tracking Team: {(() => {
                        const v = String(row.notifyTrackingTeam ?? "").trim().toLowerCase();
                        return v === "yes" || v === "true" ? "yes" : "no";
                    })()}</div>
                </div>
            )}

            {/* Error Message */}
            {row.error && (
                <div className="bg-desaturated-red border border-dark-red rounded p-2 text-xs text-dark-red">
                    {row.error}
                </div>
            )}

            {/* Success - Show Alert ID */}
            {row.alertID && (
                <div>
                    <div className="text-xs font-semibold text-grey mb-1">Alert ID</div>
                    <div className="text-xs">{row.alertID}</div>
                </div>
            )}

            {/* KML Preview Modal */}
            {showPreview && row.customShapes && (
                <KMLPreviewModal
                    shapes={row.customShapes}
                    onClose={() => setShowPreview(false)}
                />
            )}
        </div>
    );
};

export const ImportSiteAlerts: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prevUrl = searchParams.get("prev") ?? "/site-alerts";
    const {isSuperAdmin, clientID: authClientID} = useAuth();

    const [client, setClient] = useState<Client | null | undefined>(undefined);
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [parsedData, setParsedData] = useState<ImportRow[]>([]);
    const parsedDataRef = useRef<ImportRow[]>([]);
    const [isImporting, setIsImporting] = useState(false);
    const [stats, setStats] = useState<ImportStats>({
        total: 0,
        pending: 0,
        processing: 0,
        success: 0,
        error: 0
    });

    const effectiveClientID = isSuperAdmin ? client?.id : authClientID;
    const {hasSamdeskFeeds, hasIntelligenceFusionFeeds} = useIncidentFeeds(effectiveClientID);

    // Keep ref in sync with state
    useEffect(() => {
        parsedDataRef.current = parsedData;
    }, [parsedData]);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const parseFile = useCallback((uploadedFile: File) => {
        const fileExtension = uploadedFile.name.split(".").pop()?.toLowerCase();

        if (fileExtension === "csv") {
            // Parse CSV
            Papa.parse(uploadedFile, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    const rows: ImportRow[] = results.data.map((row: any, index: number) => ({
                        ...row,
                        rowNumber: index + 1,
                        status: "pending" as const
                    }));
                    setParsedData(rows);
                    setStats({
                        total: rows.length,
                        pending: rows.length,
                        processing: 0,
                        success: 0,
                        error: 0
                    });
                },
                error: (error) => {
                    alert(`Error parsing CSV: ${error.message}`);
                }
            });
        } else if (fileExtension === "xlsx" || fileExtension === "xls") {
            // Parse Excel
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, {type: "array"});
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet);

                    const rows: ImportRow[] = jsonData.map((row: any, index: number) => ({
                        ...row,
                        rowNumber: index + 1,
                        status: "pending" as const
                    }));
                    setParsedData(rows);
                    setStats({
                        total: rows.length,
                        pending: rows.length,
                        processing: 0,
                        success: 0,
                        error: 0
                    });
                } catch (error) {
                    alert(`Error parsing Excel file: ${error}`);
                }
            };
            reader.readAsArrayBuffer(uploadedFile);
        } else {
            alert("Please upload a CSV or Excel file");
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            parseFile(droppedFile);
        }
    }, [parseFile]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            parseFile(selectedFile);
        }
    }, [parseFile]);

    const updateRowStatus = useCallback((index: number, status: ImportRow["status"], error?: string, alertID?: string) => {
        const oldStatus = parsedDataRef.current[index]?.status;

        setParsedData(prev => {
            const newData = [...prev];
            newData[index] = {...newData[index], status, error, alertID};
            return newData;
        });

        // Update stats based on the actual old status from ref
        setStats(prevStats => {
            const newStats = {...prevStats};
            if (oldStatus) newStats[oldStatus]--;
            newStats[status]++;
            return newStats;
        });
    }, []);

    const handleReset = useCallback(() => {
        setFile(null);
        setParsedData([]);
        setStats({
            total: 0,
            pending: 0,
            processing: 0,
            success: 0,
            error: 0
        });
    }, []);

    const validateRow = useCallback((row: ImportRow): string | undefined => {
        // Required fields
        if (!row.name || row.name.trim() === "") {
            return "Alert name is required";
        }

        if (!row.locationType) {
            return "Location type is required";
        }

        // incidentFeedIDs is now optional - backend will default to isDefault feed if not provided

        // Location-specific validation
        if (row.locationType === "country") {
            if (!row.countryCodes || row.countryCodes.trim() === "") {
                return "Country codes are required for country location type";
            }
        } else if (row.locationType === "facility") {
            if (!row.facilityIDs || row.facilityIDs.trim() === "") {
                return "Facility IDs are required for facility location type";
            }
        } else if (row.locationType === "custom") {
            if (!row.kmlFile) {
                return "KML/KMZ file is required for custom location type";
            }
            if (!row.customShapes || row.customShapes.length === 0) {
                return "KML file must contain valid shapes";
            }
        } else {
            return `Invalid location type: ${row.locationType}. Must be country, facility, or custom`;
        }

        // Validate that at least one incident filter is specified
        const hasIncidentTags = row.incidentTags && row.incidentTags.trim() !== "";
        const hasIncidentTypes = (row.incidentTypes || row.incidentCategories) &&
            (row.incidentTypes?.trim() || row.incidentCategories?.trim());

        if (!hasIncidentTags && !hasIncidentTypes) {
            return "At least one incident filter is required (incidentTags or incidentTypes)";
        }

        // Tag mode validation
        if (row.incidentTags && row.incidentTagMode) {
            if (row.incidentTagMode !== "any" && row.incidentTagMode !== "all") {
                return `Invalid incident tag mode: ${row.incidentTagMode}. Must be 'any' or 'all'`;
            }
        }

        // Severity validation
        if (row.incidentSeverities) {
            const validSeverities = ["extreme", "major", "moderate", "limited", "insignificant", "unknown"];
            const severities = row.incidentSeverities.split(",").map(s => s.trim().toLowerCase());
            const invalid = severities.find(s => !validSeverities.includes(s));
            if (invalid) {
                return `Invalid severity: ${invalid}. Must be one of: ${validSeverities.join(", ")}`;
            }
        }

        return undefined; // No errors
    }, []);

    const fetchFeedTypes = useCallback(async (feedIDs: string[]): Promise<{ type: string; allSameType: boolean }> => {
        try {
            const feedPromises = feedIDs.map(id => apiV2.get(`/incident-feeds/${id}`));
            const responses = await Promise.all(feedPromises);
            const feeds = responses.map(r => r.data);

            if (feeds.length === 0) {
                return {type: "all", allSameType: false};
            }

            const firstType = feeds[0].type;
            const allSameType = feeds.every(f => f.type === firstType);

            return {type: firstType, allSameType};
        } catch (error) {
            console.error("Error fetching feed types:", error);
            throw new Error("Failed to fetch incident feed information");
        }
    }, []);

    const handleStartImport = useCallback(async () => {
        if (!client && isSuperAdmin) {
            alert("Please select a client first");
            return;
        }

        setIsImporting(true);

        // Process each row sequentially
        for (let i = 0; i < parsedDataRef.current.length; i++) {
            const row = parsedDataRef.current[i];

            // Skip already completed or errored rows
            if (row.status === "success" || row.status === "error") {
                continue;
            }

            // Set to processing
            updateRowStatus(i, "processing");

            try {
                // Validate row
                const validationError = validateRow(row);
                if (validationError) {
                    updateRowStatus(i, "error", validationError);
                    continue;
                }

                // Fetch feed types (only if feedIDs provided)
                let feedType = "all";

                if (row.incidentFeedIDs && row.incidentFeedIDs.trim() !== "") {
                    const feedIDs = row.incidentFeedIDs.split(",").map(f => f.trim());
                    const feedTypeResult = await fetchFeedTypes(feedIDs);

                    if (!feedTypeResult.allSameType) {
                        updateRowStatus(i, "error", "All feeds must be of the same type (all intelligence_fusion or all samdesk)");
                        continue;
                    }

                    feedType = feedTypeResult.type;
                }

                // Prepare payload
                const payload: any = {
                    name: row.name,
                    locationType: row.locationType,
                };

                // Add client ID
                if (client?.id) {
                    payload.clientID = client.id;
                }

                if (row.departmentID && row.departmentID.trim() !== "") {
                    payload.departmentID = row.departmentID.trim();
                }

                // Location-specific fields
                if (row.locationType === "country") {
                    payload.countryISOs = row.countryCodes?.split(",").map(c => c.trim());
                } else if (row.locationType === "facility") {
                    payload.facilityIDs = row.facilityIDs?.split(",").map(f => f.trim());
                    if (row.radius) {
                        payload.radius = typeof row.radius === "number" ? row.radius : parseInt(row.radius);
                    }
                    if (row.notifyFacilityGuardians) {
                        const v = String(row.notifyFacilityGuardians).trim().toLowerCase();
                        payload.notifyFacilityGuardians = v === "yes" || v === "true";
                    }
                } else if (row.locationType === "custom") {
                    payload.customShapes = row.customShapes;
                }

                // Incident configuration
                if (row.incidentFeedIDs && row.incidentFeedIDs.trim() !== "") {
                    const feedIDs = row.incidentFeedIDs.split(",").map(f => f.trim());
                    payload.incidentFeedIDs = feedIDs;
                }

                // Handle incident tags (for samdesk feeds)
                if (row.incidentTags) {
                    payload.incidentTags = row.incidentTags.split(",").map(t => t.trim());
                }

                if (row.excludedTags) {
                    payload.excludedTags = row.excludedTags.split(",").map(t => t.trim());
                }

                if (row.incidentTagMode) {
                    payload.incidentTagMode = row.incidentTagMode;
                }

                // Handle incident types and categories (for intelligence_fusion feeds)
                if (row.incidentTypes && row.incidentTypes.trim() !== "") {
                    payload.incidentTypes = row.incidentTypes.split(",").map(t => t.trim());
                }
                if (row.incidentCategories && row.incidentCategories.trim() !== "") {
                    payload.incidentCategories = row.incidentCategories.split(",").map(t => t.trim());
                }

                if (row.incidentSeverities) {
                    payload.incidentSeverities = row.incidentSeverities.split(",").map(s => s.trim().toLowerCase());
                }

                // Auto-detect incidentType mode based on what's populated
                if (payload.incidentTags && payload.incidentTags.length > 0) {
                    payload.incidentType = "tags";
                } else if (payload.incidentTypes && payload.incidentTypes.length > 0) {
                    payload.incidentType = "categories";
                } else {
                    payload.incidentType = "all";
                }

                if (row.alertRecipients) {
                    payload.alertRecipients = row.alertRecipients.split(",").map(r => r.trim());
                }

                if (row.alertNotificationGroups) {
                    payload.notificationGroupIDs = row.alertNotificationGroups.split(",").map(r => r.trim());
                }

                if (row.notifyTrackingTeam) {
                    const v = String(row.notifyTrackingTeam).trim().toLowerCase();
                    payload.notifyTrackingTeam = v === "yes" || v === "true";
                }

                // Make API call (create or update)
                let response;
                if (row.id && row.id.trim() !== "") {
                    // Update existing alert
                    response = await apiV2.patch(`/site-based-alerts/${row.id}`, payload);
                } else {
                    // Create new alert
                    response = await apiV2.post("/site-based-alerts", payload);
                }

                // Mark as success
                updateRowStatus(i, "success", undefined, response.data.id || response.data._id);
            } catch (error) {
                console.error(`Error importing row ${row.rowNumber}:`, error);
                let errorMessage = "Unknown error occurred";
                if (isAxiosError(error)) {
                    errorMessage = error.response?.data?.error || error.message;
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }
                updateRowStatus(i, "error", errorMessage);
            }
        }

        setIsImporting(false);
    }, [client, isSuperAdmin, validateRow, updateRowStatus, fetchFeedTypes]);

    const handleDownloadExampleKML = useCallback(() => {
        // Minimal KML example showing the three supported shape types spread out geographically
        // Point -> {type: "circle", center: [lat, lng], radius: 5000} (default 5000m, or custom via ExtendedData)
        // Polygon -> {type: "polygon", geometry: [[lat, lng], ...]}
        // LineString -> {type: "polyline", geometry: [[lat, lng], ...]}
        const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <Placemark>
      <name>Polygon Example</name>
      <description>A polygon defining a custom area</description>
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>
              -0.18,51.60
              -0.18,51.62
              -0.13,51.62
              -0.13,51.50
              -0.18,51.50
            </coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>
    </Placemark>
    <Placemark>
      <name>Point with Default Radius</name>
      <description>Will use default 5000m radius</description>
      <Point>
        <coordinates>-0.14,51.41</coordinates>
      </Point>
    </Placemark>
    <Placemark>
      <name>Point with Custom Radius</name>
      <description>Custom 10000m radius via ExtendedData</description>
      <ExtendedData>
        <Data name="radius">
          <value>10000</value>
        </Data>
      </ExtendedData>
      <Point>
        <coordinates>-0.02,51.41</coordinates>
      </Point>
    </Placemark>
    <Placemark>
      <name>LineString Example</name>
      <description>A line representing a route or corridor</description>
      <LineString>
        <coordinates>
          -0.10,51.54
          -0.08,51.55
          -0.06,51.54
        </coordinates>
      </LineString>
    </Placemark>
  </Document>
</kml>`;

        // Create blob and download
        const blob = new Blob([kml], {type: "application/vnd.google-earth.kml+xml"});
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", "site-alert-example.kml");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const handleDownloadExample = useCallback(() => {
        // Create example data - conditionally include fields based on available feed types
        const exampleData = [
            {
                id: "",
                name: "Example Country Alert",
                locationType: "country",
                departmentID: "",
                countryCodes: "GB,US",
                facilityIDs: "",
                radius: "",
                notifyFacilityGuardians: "",
                notifyTrackingTeam: "no",
                ...(hasSamdeskFeeds ? {
                    incidentTags: "fire,arson",
                    excludedTags: "low_severity",
                    incidentTagMode: "any"
                } : {}),
                ...(hasIntelligenceFusionFeeds ? {
                    incidentTypes: "Ambush,Assassination",
                    incidentCategories: "Direct Weapons,Bombing",
                    incidentSeverities: "extreme,major"
                } : {}),
                alertRecipients: "user-id-1,user-id-2",
                alertNotificationGroups: "group-id-1,group-id-2"
            },
            {
                id: "",
                name: "Example Facility Alert",
                locationType: "facility",
                departmentID: "",
                countryCodes: "",
                facilityIDs: "facility-id-1,facility-id-2",
                radius: "5000",
                notifyFacilityGuardians: "yes",
                notifyTrackingTeam: "yes",
                ...(hasSamdeskFeeds ? {
                    incidentTags: "",
                    excludedTags: "",
                    incidentTagMode: ""
                } : {}),
                ...(hasIntelligenceFusionFeeds ? {
                    incidentTypes: "Earthquake,Flood",
                    incidentCategories: "Hazard",
                    incidentSeverities: "extreme,major"
                } : {}),
                alertRecipients: "",
                alertNotificationGroups: ""
            },
            {
                id: "",
                name: "Example Custom Alert",
                locationType: "custom",
                departmentID: "",
                countryCodes: "",
                facilityIDs: "",
                radius: "",
                notifyFacilityGuardians: "",
                notifyTrackingTeam: "no",
                ...(hasSamdeskFeeds ? {
                    incidentTags: "natural_disaster,high_severity",
                    excludedTags: "",
                    incidentTagMode: "any"
                } : {}),
                ...(hasIntelligenceFusionFeeds ? {
                    incidentTypes: "",
                    incidentCategories: "",
                    incidentSeverities: ""
                } : {}),
                alertRecipients: "",
                alertNotificationGroups: "",
                __note: "For custom location type, upload KML/KMZ file in the tile UI. Click 'Download Example KML' below for a sample file. Valid severities: extreme, major, moderate, limited, insignificant, unknown."
            }
        ];

        // Convert to CSV
        const csv = Papa.unparse(exampleData);

        // Create blob and download
        const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", "site-alert-import-example.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [hasSamdeskFeeds, hasIntelligenceFusionFeeds]);

    const getStatusColor = (status: ImportRow["status"]) => {
        switch (status) {
            case "pending":
                return "border-light-grey bg-desaturated-grey";
            case "processing":
                return "border-blue bg-light-blue";
            case "success":
                return "border-green bg-desaturated-green";
            case "error":
                return "border-red bg-desaturated-red";
            default:
                return "border-light-grey";
        }
    };

    const getStatusBadge = (status: ImportRow["status"]) => {
        switch (status) {
            case "pending":
                return <span className="px-2 py-1 text-xs rounded-full bg-light-grey text-grey">Pending</span>;
            case "processing":
                return <span
                    className="px-2 py-1 text-xs rounded-full bg-light-blue text-dark-blue">Processing...</span>;
            case "success":
                return <span
                    className="px-2 py-1 text-xs rounded-full bg-desaturated-green text-dark-green">Success</span>;
            case "error":
                return <span className="px-2 py-1 text-xs rounded-full bg-desaturated-red text-dark-red">Error</span>;
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <nav className="flex items-center gap-2 text-sm text-light-grey">
                <Link to="/sites" className="hover:text-grey">Sites</Link>
                <span>&gt;</span>
                <Link to={prevUrl} className="hover:text-grey">Site-Based Alerts</Link>
                <span>&gt;</span>
                <span className="text-grey">Import Site Alerts</span>
            </nav>
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold">Import Site Alerts</h1>
                <div className="flex gap-2">
                    <ButtonV2
                        label="Download Example CSV"
                        onClick={handleDownloadExample}
                        disabled={isSuperAdmin && !client}
                    />
                    <ButtonV2
                        label="Download Example KML"
                        onClick={handleDownloadExampleKML}
                        disabled={isSuperAdmin && !client}
                    />
                </div>
            </div>

            {/* Client Selector */}
            {isSuperAdmin && (
                <div className="bg-white shadow-md rounded-lg p-6">
                    <ClientSelectV2
                        onChange={setClient}
                        label="Client"
                        required
                    />
                </div>
            )}

            {/* Upload Area */}
            {!file && (
                <div
                    className={`bg-white shadow-md rounded-lg p-12 border-2 border-dashed transition-colors ${
                        isDragging ? "border-blue bg-light-blue" : "border-light-grey"
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div className="flex flex-col items-center gap-4">
                        <div className="text-6xl text-light-grey">📄</div>
                        <div className="text-center">
                            <p className="text-lg font-semibold mb-2">Drop your CSV or Excel file here</p>
                            <p className="text-grey mb-4">or</p>
                            <label className="cursor-pointer">
                                <span
                                    className="px-6 py-3 bg-blue text-white rounded-md hover:bg-dark-blue inline-block">
                                    Browse Files
                                </span>
                                <input
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                            </label>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview and Import - TO BE CONTINUED */}
            {file && parsedData.length > 0 && (
                <>
                    {/* Stats */}
                    <div className="bg-white shadow-md rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">
                                {file.name} ({stats.total} rows)
                            </h2>
                            <div className="flex gap-2">
                                <ButtonV2
                                    label="Reset"
                                    onClick={handleReset}
                                    disabled={isImporting}
                                />
                                <ButtonV2
                                    label={isImporting ? "Importing..." : "Start Import"}
                                    onClick={handleStartImport}
                                    disabled={isImporting || (isSuperAdmin && !client) || stats.pending === 0}
                                />
                            </div>
                        </div>

                        {/* Warning message if super admin hasn't selected client */}
                        {isSuperAdmin && !client && (
                            <div
                                className="mb-4 p-3 bg-light-orange border border-dark-orange rounded-md text-dark-orange">
                                Please select a client before starting the import.
                            </div>
                        )}

                        {/* Progress Stats */}
                        <div className="grid grid-cols-4 gap-4 mb-4">
                            <div className="bg-desaturated-grey p-4 rounded-md">
                                <div className="text-2xl font-bold text-grey">{stats.pending}</div>
                                <div className="text-sm text-grey">Pending</div>
                            </div>
                            <div className="bg-light-blue p-4 rounded-md">
                                <div className="text-2xl font-bold text-dark-blue">{stats.processing}</div>
                                <div className="text-sm text-dark-blue">Processing</div>
                            </div>
                            <div className="bg-desaturated-green p-4 rounded-md">
                                <div className="text-2xl font-bold text-dark-green">{stats.success}</div>
                                <div className="text-sm text-dark-green">Success</div>
                            </div>
                            <div className="bg-desaturated-red p-4 rounded-md">
                                <div className="text-2xl font-bold text-dark-red">{stats.error}</div>
                                <div className="text-sm text-dark-red">Error</div>
                            </div>
                        </div>
                    </div>

                    {/* Tiles Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {parsedData.map((row, index) => (
                            <ImportTile
                                key={index}
                                row={row}
                                index={index}
                                onKMLFileChange={async (file, shapes) => {
                                    setParsedData(prev => {
                                        const newData = [...prev];
                                        newData[index] = {
                                            ...newData[index],
                                            kmlFile: file,
                                            customShapes: shapes
                                        };
                                        return newData;
                                    });
                                }}
                                getStatusColor={getStatusColor}
                                getStatusBadge={getStatusBadge}
                                isImporting={isImporting}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
